// src/pages/TrendPage.js
import React, { memo, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Spacer from "../components/ui/Spacer";
import LoadingBouncyDots from "../components/ui/LoadingBouncyDots";

import { useUserDoc } from "../hooks/useUserDoc";
import { useActivityFeed } from "../context/ActivityFeedContext";
import { usePullToRefresh } from "../hooks/usePullToRefresh";
import { useAccessControl } from "../hooks/useAccessControl";

import TrendSection from "../components/trends/TrendSection";
import MetricTrendChart from "../components/charts/MetricTrendChart";
import MetricLineChart from "../components/charts/MetricLineChart";
import MetricBarChart from "../components/charts/MetricBarChart";
import { LockOverlay } from "../components/ui/LockOverlay";
import { ChartBlock } from "../components/result/ChartBlock";

import { useExertionTrendData } from "../hooks/useExertionTrendData";
import { useExertionVentilationCompare } from "../hooks/useExertionVentilationCompare";

import { getPrimaryColour } from "../utils/getPrimaryColour";
import {
  buildSessionColourMap,
  applySessionColoursToLineDatasets,
  applySessionColoursToBarDatasets,
  buildSessionColours,
} from "../utils/sessionColours";
import { useUserEmail } from "../hooks/useUserEmail";

const ADVANCED_ALLOWED_EMAILS = new Set([
  "bendruce@icloud.com",
  "lmogg@electronrx.com",
  "kgozen@electronrx.com",
]);

function TrendPage() {
  const { t } = useTranslation();
  const { data } = useUserDoc();
  const {
    loading: loadingFeed,
    error: feedError,
    items,
    refreshNow,
  } = useActivityFeed();
  const { lockResults, plan, totalAny } = useAccessControl();
  // If you later want to actually lock trends, switch this to `lockResults`
  const lockTrends = false; // lockResults
  const { email: userEmail, loading: loadingEmail } = useUserEmail();

  // Pull-to-refresh on the app scroll container
  const [containerEl, setContainerEl] = useState(null);
  useEffect(() => setContainerEl(document.getElementById("app-scroll")), []);
  const { offset, isRefreshing } = usePullToRefresh({
    container: containerEl,
    onRefresh: refreshNow,
    threshold: 72,
    maxPull: 140,
  });

  useEffect(() => {
    refreshNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasAnyRecordings =
    !loadingFeed && !feedError && (items?.length || 0) > 0;

  const onboardingStep = data?.onboarding?.step;
  const inCheckoutFlow = useMemo(
    () =>
      ["planSelected", "accountCreated", "paymentComplete"].includes(
        onboardingStep
      ),
    [onboardingStep]
  );

  // ---- New exertion trends + comparison data (single source of truth) ----
  const uid =
    data?.uid ||
    data?.userId ||
    data?.profile?.uid ||
    items?.[0]?.uid ||
    items?.[0]?.userId ||
    items?.[0]?.profile?.uid;

  const {
    loading: loadingExertion,
    docs: exertionDocs,
    rrSeries: rr5minSeries,
    br1Series,
    br2Series,
    rt90Series,
    rt95Series,
  } = useExertionTrendData({
    uid,
    items,
    repo: "pdx-recording-exertion-recovery",
    tag: "breatheultra",
    maxDocs: 30,
  });

  const { cumulativeDatasets, bins30s } = useExertionVentilationCompare({
    docs: exertionDocs,
    maxCompare: 8,
    binSize: 30,
    maxT: 300,
  });

  const showTrends = Array.isArray(items) && items.length > 0;

  const primary = getPrimaryColour();

  const sessionColours = useMemo(() => {
    return buildSessionColours((cumulativeDatasets || []).length, primary);
  }, [cumulativeDatasets, primary]);

  const colouredCumulativeDatasets = useMemo(() => {
    return applySessionColoursToLineDatasets(
      cumulativeDatasets,
      sessionColours,
      primary
    );
  }, [cumulativeDatasets, sessionColours, primary]);

  const colouredBins30s = useMemo(() => {
    return {
      ...bins30s,
      datasets: applySessionColoursToBarDatasets(
        bins30s?.datasets,
        sessionColours,
        primary
      ),
    };
  }, [bins30s, sessionColours, primary]);

  const canSeeAdvanced =
    typeof userEmail === "string" &&
    ADVANCED_ALLOWED_EMAILS.has(userEmail.toLowerCase());

  return (
    <div className="relative w-full max-w-lg h-fit flex flex-col p-4 gap-4 z-10 mx-auto">
      {/* Pull-to-refresh indicator */}
      <div
        className="pointer-events-none -mt-4 flex w-full items-end justify-center"
        style={{
          height: Math.max(0, offset),
          transition: offset === 0 ? "height 160ms ease-out" : "none",
        }}
        aria-hidden="true"
      >
        {(offset > 0 || isRefreshing) && (
          <LoadingBouncyDots
            text={
              isRefreshing
                ? t("activity.refreshing", { defaultValue: "Refreshing…" })
                : t("activity.pullToRefresh", {
                    defaultValue: "Pull to refresh",
                  })
            }
            size={6}
            gap={3}
            speed={700}
            className="mb-2"
            textClassName="text-xs text-typography/60"
          />
        )}
      </div>

      {/* Empty state */}
      {!hasAnyRecordings && !loadingFeed && !feedError && (
        <>
          <div className="rounded-2xl flex flex-col gap-4 h-fit items-start">
            <div>
              <h3 className="text-lg font-semibold text-typography">
                {t("emptyState.title", { defaultValue: "Try your first test" })}
              </h3>
              <p className="text-sm text-typography/80">
                {t("emptyState.copy", {
                  defaultValue:
                    "No recordings yet. Head to the recording page to get started.",
                })}
              </p>
            </div>
            <div className="flex items-center gap-4 justify-end">
              <Link
                to="/recording"
                className="primary-button px-6 py-3 text-sm md:text-base"
                aria-label={t("emptyState.ctaAria", {
                  defaultValue: "Go to recording to try the product",
                })}
              >
                {t("emptyState.cta", { defaultValue: "Record" })}
              </Link>
            </div>
          </div>
          <Spacer />
        </>
      )}

      {/* Upsell */}
      {plan === "none" && totalAny > 0 && (
        <>
          <div className="rounded-2xl flex flex-col gap-4 h-fit items-start">
            <h3 className="subtitle-text">
              {t("upsell.firstUsed.title", {
                defaultValue: "You're off to a great start",
              })}
            </h3>
            <p>
              {t("upsell.firstUsed.copy", {
                defaultValue:
                  "Choose a plan to keep recording and unlock insights.",
              })}
            </p>
            <div className="flex justify-end">
              <Link to="/payment" className="primary-button px-4 py-2 text-sm">
                {t("upsell.choosePlan", { defaultValue: "Choose a plan" })}
              </Link>
            </div>
          </div>
          <Spacer />
        </>
      )}

      {/* === Exertion Trends (new) === */}
      {showTrends && (
        <>
          {canSeeAdvanced && (
            <>
              <ChartBlock
                title={t("trends.minuteVentilationCompare.title", {
                  defaultValue: "Ventilation Comparison",
                })}
                infoKey="info.minuteVentilation"
              >
                <MetricLineChart
                  height={400}
                  datasets={colouredCumulativeDatasets}
                  preset="none"
                  showLegend={true}
                  showTooltip={false}
                  xType="linear"
                  xMin={0}
                  xMax={300}
                  xTickStep={60}
                  xTickFormat={(v) => `${v}s`}
                  yDecimals={0}
                  xLabel={t("activity.spirometry.timeLabel", {
                    defaultValue: "Time (s)",
                  })}
                />
                <LockOverlay show={lockTrends} variant="comingSoon" />
              </ChartBlock>
              <ChartBlock
                title={t("trends.minuteVentilationBinsCompare.title", {
                  defaultValue: "Ventilation by time block",
                })}
                infoKey="info.minuteVentilationBins30s"
              >
                <MetricBarChart
                  height={400}
                  labels={colouredBins30s.labels}
                  datasets={colouredBins30s.datasets}
                  showLegend={true}
                  showTooltip={false}
                  xLabel={t("activity.spirometry.timeLabel", {
                    defaultValue: "Time (s)",
                  })}
                />
                <LockOverlay show={lockTrends} variant="comingSoon" />
              </ChartBlock>
            </>
          )}
          {/* RR trend (post-exertion RR at 5 min, derived from estimated_rr_bpm) */}
          <ChartBlock
            title={t("trends.rr.title", { defaultValue: "Resp. Rate" })}
            infoKey="info.respiratoryRate"
          >
            <MetricTrendChart
              height={200}
              labels={rr5minSeries.labels}
              values={rr5minSeries.values}
              yUnit={t("trends.rr.unit", { defaultValue: "breaths/min" })}
              lineLabel={t("trends.rr.label", {
                defaultValue: "Resp. Rate",
              })}
              loading={loadingFeed || loadingExertion}
              yDecimals={0}
            />
            <LockOverlay show={lockTrends} variant="comingSoon" />
          </ChartBlock>
          <ChartBlock
            title={t("metrics.breathingRecovery60.name", {
              defaultValue: "Breathing Recovery (1 min)",
            })}
            infoKey="info.breathingRecovery"
          >
            <MetricTrendChart
              height={200}
              labels={br1Series.labels}
              values={br1Series.values}
              yUnit={t("trends.recovery.unit", { defaultValue: "bpm drop" })}
              lineLabel={t("metrics.breathingRecovery60.name", {
                defaultValue: "Breathing Recovery (1 min)",
              })}
              loading={loadingFeed || loadingExertion}
              yMin={0}
              yDecimals={2}
            />
            <LockOverlay show={lockTrends} variant="comingSoon" />
          </ChartBlock>
          <ChartBlock
            title={t("metrics.breathingRecovery120.name", {
              defaultValue: "Breathing Recovery (2 min)",
            })}
            infoKey="info.breathingRecovery"
          >
            <MetricTrendChart
              height={200}
              labels={br2Series.labels}
              values={br2Series.values}
              yUnit={t("trends.recovery.unit", { defaultValue: "bpm drop" })}
              lineLabel={t("metrics.breathingRecovery120.name", {
                defaultValue: "Breathing Recovery (2 min)",
              })}
              loading={loadingFeed || loadingExertion}
              yMin={0}
              yDecimals={2}
            />
            <LockOverlay show={lockTrends} variant="comingSoon" />
          </ChartBlock>
          <ChartBlock
            title={t("metrics.recoveryTime90.name", {
              defaultValue: "Recovery Time (90%)",
            })}
            infoKey="info.recoveryTime"
          >
            <MetricTrendChart
              height={200}
              labels={rt90Series.labels}
              values={rt90Series.values}
              yUnit={t("trends.recTime.unit", { defaultValue: "mm:ss" })}
              lineLabel={t("metrics.recoveryTime90.name", {
                defaultValue: "Recovery Time (90%)",
              })}
              loading={loadingFeed || loadingExertion}
              yMin={0}
              yDecimals={0}
              yTickStep={30}
            />
            <LockOverlay show={lockTrends} variant="comingSoon" />
          </ChartBlock>
          <ChartBlock
            title={t("metrics.recoveryTime95.name", {
              defaultValue: "Recovery Time (95%)",
            })}
            infoKey="info.recoveryTime"
          >
            <MetricTrendChart
              height={200}
              labels={rt95Series.labels}
              values={rt95Series.values}
              yUnit={t("trends.recTime.unit", { defaultValue: "mm:ss" })}
              lineLabel={t("metrics.recoveryTime95.name", {
                defaultValue: "Recovery Time (95%)",
              })}
              loading={loadingFeed || loadingExertion}
              yMin={0}
              yDecimals={0}
              yTickStep={30}
            />
            <LockOverlay show={lockTrends} variant="comingSoon" />
          </ChartBlock>
        </>
      )}

      {/* Errors */}
      {feedError && (
        <div className="text-xs text-red-600">
          {t("activity.loadError", { defaultValue: "Could not load items." })}{" "}
          {String(feedError.message || feedError)}
        </div>
      )}
    </div>
  );
}

export default memo(TrendPage);
